import { useState } from 'react'
import { ArrowLeft, Factory, Home, MapPin, Phone, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  SupplierDeleteConfirmDialog,
  type PendingSupplierDelete,
} from '@/features/suppliers/components/supplier-delete-confirm-dialog'
import {
  SupplierOrganizationFormProvider,
  useSupplierOrganizationForm,
} from '@/features/suppliers/context/supplier-organization-form-context'
import { useSupplierOrganizationMutations } from '@/features/suppliers/hooks/use-supplier-organizations'
import { AccountSettingsSidebarNav } from '@/features/settings/components/account-settings-sidebar-nav'
import { handleServerError } from '@/shared/lib/handle-server-error'

function SupplierOrganizationLayoutContent() {
  const { t } = useTranslation(['installations', 'common'])
  const navigate = useNavigate()
  const { installationId } = useParams<{ installationId: string }>()
  const { supplierOrganizationId, state, canEdit } = useSupplierOrganizationForm()
  const { deleteSupplierOrganization } = useSupplierOrganizationMutations()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const suppliersPath = `/dashboard/${installationId}/suppliers`
  const supplierBase = `/dashboard/${installationId}/suppliers/${supplierOrganizationId}`

  const sidebarNavItems = [
    {
      title: t('installations:suppliers.edit.nav.home'),
      href: `${supplierBase}/home`,
      icon: <Home size={18} />,
    },
    {
      title: t('installations:suppliers.edit.nav.address'),
      href: `${supplierBase}/address`,
      icon: <MapPin size={18} />,
    },
    {
      title: t('installations:suppliers.edit.nav.contact'),
      href: `${supplierBase}/contact`,
      icon: <Phone size={18} />,
    },
    {
      title: t('installations:suppliers.edit.nav.installations'),
      href: `${supplierBase}/installations`,
      icon: <Factory size={18} />,
    },
  ]

  const pendingDelete: PendingSupplierDelete = {
    id: supplierOrganizationId,
    label: state.name,
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteSupplierOrganization.mutateAsync(supplierOrganizationId)
      setDeleteDialogOpen(false)
      navigate(suppliersPath)
    } catch (error) {
      handleServerError(error)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
      <div className="shrink-0 space-y-4">
        <Button type="button" variant="ghost" className="w-fit px-0" asChild>
          <Link to={suppliersPath}>
            <ArrowLeft className="size-4" />
            {t('installations:suppliers.edit.backToList')}
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-0.5">
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
              {t('installations:suppliers.edit.title', { name: state.name })}
            </h1>
            <p className="text-muted-foreground">
              {t('installations:suppliers.edit.description')}
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
              aria-label={t('installations:suppliers.a11y.deleteSupplier', {
                name: state.name,
              })}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleteSupplierOrganization.isPending}
            >
              <Trash2 className="size-4" />
              {t('common:actions.delete')}
            </Button>
          )}
        </div>
      </div>
      <Separator className="my-4 shrink-0 lg:my-6" />
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden md:gap-2 lg:flex-row lg:gap-0 lg:space-x-12">
        <aside className="top-0 shrink-0 lg:sticky lg:w-1/5">
          <AccountSettingsSidebarNav items={sidebarNavItems} />
        </aside>
        <div className="flex min-h-0 w-full flex-1 overflow-y-hidden p-1">
          <Outlet />
        </div>
      </div>

      <SupplierDeleteConfirmDialog
        pendingDelete={pendingDelete}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isLoading={deleteSupplierOrganization.isPending}
      />
    </div>
  )
}

export function SupplierOrganizationLayout() {
  return (
    <SupplierOrganizationFormProvider>
      <SupplierOrganizationLayoutContent />
    </SupplierOrganizationFormProvider>
  )
}
